import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { mergeRefs } from '../../utils/merge-refs';
import { StyledDurationInput } from './styled';

export type DurationInputProps = React.HTMLAttributes<HTMLInputElement> & {
  minMinutes?: number;
  maxMinutes?: number;
  onChange?: (value: unknown) => void;
  defaultValue?: number;
  required?: boolean;
  disabled?: boolean;
};

export const DurationInput = forwardRef<HTMLInputElement, DurationInputProps>(
  ({ defaultValue, onChange, ...rest }, ref) => {
    const defaultText = getDefaultText(defaultValue);

    const [prevInput, setPrevInput] = useState<string>(defaultText);
    const [duration, setDuration] = useState<number | null>(defaultValue ?? 0);
    const inputDuration = useRef<HTMLInputElement>(null);

    const handleChange = useCallback((): void => {
      const inputValue = inputDuration?.current?.value;

      if (inputDuration?.current?.value === '') {
        setPrevInput('');
        setDuration(0);
        return;
      }

      if (typeof inputValue === 'string' && inputDuration?.current?.value) {
        const regMatch = inputValue.match(
          /^(\d{1,2})(:{0,1})([0-5][0-9]{0,1}){0,1}$/
        );

        if (regMatch) {
          const inputHours = regMatch[1];
          const inputMinutes = regMatch[3];
          inputDuration.current.value = `${inputHours}${
            inputMinutes ? `:${inputMinutes}` : ''
          }`;

          const hours = Number(inputHours) * 60;
          const minutes = Number(inputMinutes ?? 0);

          setDuration(hours + minutes);
          setPrevInput(inputDuration.current.value);
        } else {
          inputDuration.current.value = prevInput;
        }
      }
    }, [prevInput]);

    useEffect(() => {
      handleChange();
      if (onChange) {
        onChange(duration);
      }
    }, [duration, handleChange, onChange]);

    return (
      <StyledDurationInput
        {...rest}
        type="text"
        ref={mergeRefs(ref, inputDuration)}
        onChange={handleChange}
        defaultValue={defaultText}
      />
    );
  }
);

function getDefaultText(durationInMinutes?: number): string {
  if (!durationInMinutes || typeof durationInMinutes !== 'number') {
    return '';
  }

  const minutes = durationInMinutes % 60;
  const hours = (durationInMinutes - minutes) / 60;
  return `${hours > 9 ? hours : `0${hours}`}:${
    minutes > 9 ? minutes : `0${minutes}`
  }`;
}
